import { http } from '@/shared/api/api-client';
import type { PagedResponse } from '@/shared/api/api-types';

import type { AuditEntry, AuditLogParams } from '../types/audit-log.types';

/**
 * The Admin audit log — GET /admin/audit-log (reports.audit.read).
 *
 * UT system/AdminApis/Control/Controllers/AdminAuditLogController.cs. The
 * backend limits entries to the staff member's countries; entries without a
 * country (staff, providers) are visible to every reader.
 */

interface AdminPage<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export async function getAuditLog(
  params: AuditLogParams,
  signal?: AbortSignal,
): Promise<PagedResponse<AuditEntry>> {
  const page = await http.get<AdminPage<AuditEntry>>('/admin/audit-log', { params, signal });
  return {
    items: page.items,
    page: page.page,
    pageSize: page.pageSize,
    totalItems: page.totalCount,
    totalPages: page.totalPages,
  };
}

/** The same search as Excel (reports.export). The backend records the export itself. */
export async function exportAuditLog(params: Omit<AuditLogParams, 'page' | 'pageSize'>) {
  const { blob, fileName } = await http.download('/admin/audit-log/export', {
    params: { ...params, format: 'xlsx' },
  });
  return { blob, fileName: fileName ?? 'audit-log.xlsx' };
}

/**
 * An item's History — GET /admin/history/{type}/{id} (D-26). Readable with the
 * item's own read permission, limited to your countries, with the recorded
 * values your role may not see removed (listed in `redactedFields`).
 */
export async function getEntityHistory(
  entityType: string,
  entityId: string,
  page: number,
  pageSize: number,
  signal?: AbortSignal,
): Promise<PagedResponse<AuditEntry>> {
  const result = await http.get<AdminPage<AuditEntry>>(
    `/admin/history/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
    { params: { page, pageSize }, signal },
  );
  return {
    items: result.items,
    page: result.page,
    pageSize: result.pageSize,
    totalItems: result.totalCount,
    totalPages: result.totalPages,
  };
}
